import { EventManager, models as eventManagerModels, SourceEventManager } from '@kurtosys/event-manager';
import { IEventTypeCache, IListenForOptions } from '@kurtosys/types/app/IListenForOptions.js';
import { WindowManager } from '@kurtosys/window-manager';
import stringify from 'json-stable-stringify';
import { computed, observable } from 'mobx';

import { ILogOptions, TLogType } from '../appStartUtils/log.js';
import { IComponentStylesBase } from '../models/IComponentStylesBase.js';

import { AppStoreBase } from './base/AppStoreBase.js';
import { StoreBase } from './base/StoreBase.js';
import { InputStore } from './InputStore.js';

type EventTypeDetail = eventManagerModels.EventTypeDetail;
type EventBusStoreCallback<TDetail extends EventTypeDetail> = (
	ev: eventManagerModels.EventPublish<TDetail>,
) => Promise<void>;
type EventBusStoreCallbacks<TDetail extends EventTypeDetail> = Partial<
	Record<eventManagerModels.EventTypes, EventBusStoreCallback<TDetail>>
>;
interface IListenOptions<TDetail extends EventTypeDetail> {
	callback: EventBusStoreCallback<TDetail>;
	updateRequiredPeerTracking?: boolean;
	runOnce?: boolean;
}

export class EventBusStore<TConfiguration, TComponentStyles extends IComponentStylesBase> extends StoreBase<
	TConfiguration,
	TComponentStyles
> {
	callbacks: EventBusStoreCallbacks<any> | undefined;
	eventCache: Map<string, string> = new Map();
	_eventManager: EventManager;

	@observable.ref requiredPeerCallbackTracking: Record<string, boolean> = {};

	@computed
	public get requiredPeerCallbacksCompleted() {
		if (Object.keys(this.requiredPeerCallbackTracking).length === 0) {
			return true;
		}
		let completed = true;
		for (const name in this.requiredPeerCallbackTracking) {
			if (!this.requiredPeerCallbackTracking[name]) {
				completed = false;
				break;
			}
		}
		return completed;
	}

	/**
	 * Set this to true if we want the AppStoreBase to track child app initializations (e.g. grid app)
	 */
	private isChildCallbackTrackingRequired = false;
	/**
	 * If isChildCallbackTrackingRequired is true, the AppStoreBase will track this boolean to indicate to the app when it should bootstrap.
	 */
	private areChildCallbacksCompleted = false;

	/**
	 * Use to validate if child callbacks are completed but also considering if we should be tracking it.
	 *
	 * @readonly
	 */
	public get requiredChildCallbacksCompleted() {
		if (!this.isChildCallbackTrackingRequired) {
			return true;
		}
		return this.areChildCallbacksCompleted;
	}

	/**
	 * Call this if you require child callback tracking
	 */
	public enableChildCallbackTracking() {
		this.isChildCallbackTrackingRequired = true;
	}

	/**
	 * Update areChildCallbacksCompleted, which will be tracked via requiredChildCallbacksCompleted in the AppStoreBase
	 *
	 * @param state
	 */
	public setAreChildCallbacksCompleted(state: boolean) {
		this.areChildCallbacksCompleted = state;
	}

	get eventManager(): EventManager {
		if (!this._eventManager) {
			this._eventManager = EventManager.get();
		}
		return this._eventManager;
	}

	@computed
	get appStore() {
		return this.storeContext.get<AppStoreBase<TConfiguration, TComponentStyles>>('appStore');
	}

	@computed
	get inputStore() {
		return this.storeContext.get<InputStore<TConfiguration, TComponentStyles>>('inputStore');
	}

	@computed
	get listenFor(): IListenForOptions[] {
		const appStore = this.storeContext.get<AppStoreBase<TConfiguration, TComponentStyles>>('appStore');
		const legacyEventAppIds = appStore.globalInputIdentifiers || [];
		const listenFor = appStore.listenFor;
		// Add support for legacy globalInputIdentifiers
		if (legacyEventAppIds.length > 0) {
			for (const appId of legacyEventAppIds) {
				const existingListener = listenFor.find((listener) => listener.appId === appId);
				if (!existingListener) {
					listenFor.push({
						appId,
						eventTypes: [
							{
								name: eventManagerModels.EventTypes.GLOBAL_INPUTS_CHANGE,
							},
						],
					});
				} else if (
					!existingListener.eventTypes.find(
						(type) => type.name === eventManagerModels.EventTypes.GLOBAL_INPUTS_CHANGE,
					)
				) {
					existingListener.eventTypes.push({
						name: eventManagerModels.EventTypes.GLOBAL_INPUTS_CHANGE,
					});
				}
			}
		}
		return listenFor;
	}

	async initialize(callbacks: EventBusStoreCallbacks<any> = {}) {
		const defaultEvents = {};

		if (this.isListeningForEvent(eventManagerModels.EventTypes.GLOBAL_INPUTS_CHANGE)) {
			defaultEvents[eventManagerModels.EventTypes.GLOBAL_INPUTS_CHANGE] = this.inputStore.handleUpdate;
		}

		if (this.isListeningForEvent(eventManagerModels.EventTypes.GLOBAL_SEARCH_CHANGE)) {
			defaultEvents[eventManagerModels.EventTypes.GLOBAL_SEARCH_CHANGE] = this.appStore.handleSearchTermUpdate;
		}

		if (
			this.isListeningForEvent(eventManagerModels.EventTypes.PAGINATION_CHANGE) &&
			this.appStore.paginationStore
		) {
			defaultEvents[eventManagerModels.EventTypes.PAGINATION_CHANGE] =
				this.appStore.paginationStore.handleChangeEvent;
		}

		this.callbacks = {
			...defaultEvents,
			...callbacks,
		};
	}

	isListeningFor(appId: string, eventName: string): boolean {
		const app = this.listenFor.find((appConfig) => {
			return appConfig.appId === appId;
		});
		const eventConfig =
			app &&
			app.eventTypes.find((eventConfig) => {
				return eventConfig.name === eventName;
			});

		return Boolean(eventConfig);
	}

	isListeningForEvent(eventType: eventManagerModels.EventTypes) {
		return this.listenFor.some((listener) => listener.eventTypes.some((et) => et.name === eventType));
	}

	/**
	 * Start listening for events that are required for initial load
	 */
	public register() {
		const listenForEvents = this.listenFor;
		if (listenForEvents.length > 0 && this.eventManager !== undefined) {
			listenForEvents.forEach((listenForEvent) => {
				const appId = listenForEvent.appId;
				const appEventTypes = listenForEvent.eventTypes;

				if (appEventTypes.length > 0) {
					appEventTypes.forEach((appEvent) => {
						const eventType = appEvent.name as eventManagerModels.EventTypes;
						const requiredForInitialLoad = appEvent.requiredForInitialLoad;
						const runOnce = appEvent.runOnce;
						const cache = appEvent.cache;
						// Wait for the eventBus Store to retrieve all events that are required before continuing with initialzing of the app
						const targetCallback = this.callbacks && this.callbacks[eventType];
						if (targetCallback) {
							const storageValue = this.getStorageValue(appId, eventType, cache);

							if (storageValue && this.hasEventDetailChanged(storageValue.detail, eventType)) {
								this.log('debug', { message: `Found storage for app ${appId} and event ${eventType}` });
								targetCallback({
									detail: storageValue,
									source: {
										templateId: `EVENT_FROM_STORAGE-${appId}_${eventType}`,
									},
								});
							}

							// Checks if the event is required and if we already have the event, then we don't need to set up additional tracking for it
							let updateRequiredPeerTracking = false;
							if (!storageValue && requiredForInitialLoad) {
								updateRequiredPeerTracking = true;
								this.requiredPeerCallbackTracking = {
									...this.requiredPeerCallbackTracking,
									[`${appId}-${eventType}`]: false,
								};
								this.log('debug', { message: `Register require event ${eventType}` });
							}

							// add an event listener if the runOnce condition hasn't already been met.
							if (!runOnce || (runOnce && !storageValue)) {
								this.log('debug', { message: `Register event ${eventType}` });
								this.listen(appId, eventType, {
									runOnce,
									updateRequiredPeerTracking,
									callback: targetCallback,
								});
							}
						}
					});
				}
			});
		}
	}

	/**
	 * Trigger event types passing the appropriate event detail, this method also handles common functionality like appending source application details.
	 *
	 * @template T
	 * @param type
	 * @param eventTypeDetail
	 */
	public trigger<T extends EventTypeDetail>(eventTypeDetail: T) {
		if (this.eventManager) {
			this.log('info', {
				message: `Triggering ${eventTypeDetail.type}`,
				detail: eventTypeDetail,
			});
			this.eventManager.publish(this.appStore.eventSourceKey, this.appStore.eventSource, eventTypeDetail);
		} else {
			this.log('warning', {
				message: `Unable to trigger event, as the event manager is not yet defined. The event manager is defined as part of the event bus store register.`,
				detail: eventTypeDetail,
			});
		}
	}

	/**
	 * Generate an application identifier based on sourced application details, which are derived from props and configuration of the source app.
	 *
	 * @param application
	 * @returns
	 */
	public generateSourceAppKey(application: eventManagerModels.EventSource): string {
		// TODO: Remove currently used in tabs (tabs to be updated to work like grid)
		const { applicationCode, applicationId, configurationKey, templateId, styleKey } = application;
		const parts = [];
		if (templateId) {
			parts.push(`ti:${templateId}`);
		}
		if (configurationKey) {
			parts.push(`ck:${configurationKey}`);
		}
		if (styleKey) {
			parts.push(`sk:${styleKey}`);
		}
		if (applicationCode) {
			parts.push(`ac:${applicationCode}`);
		}
		if (applicationId) {
			parts.push(`ai:${applicationId}`);
		}
		return parts.join('-');
	}

	/**
	 * Extract the application key from the received custom application event, which is a unique built up based on application details.
	 *
	 * This will fallback to the application id which is also derived off of the event.
	 *
	 * @private
	 * @template T
	 * @param event
	 * @returns
	 */
	public getSourceAppKeyFromEvent<T extends EventTypeDetail>(event: eventManagerModels.EventPublish<T>): string {
		// TODO: remove (used in tabs, tabs to update to work like grids)
		const { source } = event;
		let key = '';
		if (source) {
			key = this.generateSourceAppKey(source);
		}
		if (key === '') {
			key = this.getSourceAppIdFromEvent(event);
		}
		return key;
	}

	/**
	 * Retrieves the application id from event detail's application source or the legacy identifier field
	 *
	 * @private
	 * @template T
	 * @param event
	 * @returns
	 */
	public getSourceAppIdFromEvent<T extends EventTypeDetail>(event: eventManagerModels.EventPublish<T>): string {
		// TODO: Review, used in input store, can we just use SourceEventManager.generateKey?
		const { source } = event;
		if (!source) {
			return '';
		}
		if (source.applicationId) {
			return source.applicationId;
		} else {
			return SourceEventManager.generateKey(source);
		}
	}

	/**
	 * Determines Validates the to see if the last received event detail for a given event type is the same as the new event detail, regardless of source application.
	 *
	 * Note: This then keeps track of the state of the event type's last received event detail, and thus allows us to ignore duplicate updates.
	 *
	 * @param eventValue
	 * @param appId
	 * @param eventType
	 * @returns
	 */
	private hasEventDetailChanged(eventValue: Record<string, any>, eventType: string) {
		// These events should always change, regardless if they have a cached result.
		const cacheExempt: eventManagerModels.EventTypes[] = [
			eventManagerModels.EventTypes.PAGINATION_CHANGE,
			eventManagerModels.EventTypes.UPDATE_PAGINATION,
			eventManagerModels.EventTypes.CHANGE_ATTESTATION,
		];
		if (cacheExempt.find((type) => type === eventType)) {
			return true;
		}
		const key = eventType;
		const evtValue = stringify(eventValue);
		this.log('debug', { message: `event cache key ${key}` });
		if (!this.eventCache.has(key) || this.eventCache.get(key) !== evtValue) {
			this.eventCache.set(key, evtValue);
			return true;
		}
		return false;
	}

	private getStorageValue(appId: string, eventType: eventManagerModels.EventTypes, cache?: IEventTypeCache) {
		// These events should not rely on storage for for initial load.
		const cacheExempt: eventManagerModels.EventTypes[] = [eventManagerModels.EventTypes.CHANGE_ATTESTATION];
		if (cacheExempt.find((type) => type === eventType)) {
			return;
		}
		if (cache && cache.enabled === false) {
			return;
		}
		const eventDetails = WindowManager.get('eventDetails');
		if (eventDetails && eventDetails[appId] && eventDetails[appId][eventType]) {
			return eventDetails[appId][eventType];
		}
		return;
	}

	private log = (eventType: TLogType, options: ILogOptions) => {
		const appStore = this.storeContext.get<AppStoreBase<TConfiguration, TComponentStyles>>('appStore');
		const { log } = appStore;
		log(eventType, {
			...options,
			additionalContext: 'EventBusStore',
		});
	};

	private listen<T extends EventTypeDetail>(
		targetAppId: string,
		type: eventManagerModels.EventTypes,
		options: IListenOptions<T>,
	) {
		const eventManager = this.eventManager;
		if (eventManager) {
			const { callback, updateRequiredPeerTracking = false, runOnce = false } = options;
			const eventHandler = (evt: eventManagerModels.EventPublish<T>) => {
				const eventSourceAppId = evt.source.applicationId;
				if (eventSourceAppId === targetAppId) {
					this.log('success', {
						message: `Triggered ${type}`,
						detail: {
							eventSourceAppId,
							targetAppId,
							...evt.detail,
						},
					});
					if (runOnce) {
						const listener = evt.source;
						eventManager.unsubscribe(targetAppId, type, listener);
					}
					if (updateRequiredPeerTracking) {
						this.requiredPeerCallbackTracking = {
							...this.requiredPeerCallbackTracking,
							[`${targetAppId}-${type}`]: true,
						};
					}
					if (this.hasEventDetailChanged(evt.detail, type)) {
						callback(evt);
					}
				}
			};
			const subscription: eventManagerModels.EventSubscription<eventManagerModels.EventTypes, EventTypeDetail> = {
				type,
				listener: this.appStore.eventSource,
				onPublish: eventHandler,
			};
			eventManager.subscribe(targetAppId, subscription);
		} else {
			this.log('warning', {
				message: `Unable to subscribe to event type "${type}", as the event manager is not yet defined. It is defined as part of the event bus store register method.`,
			});
		}
	}
}
